import { Drawer } from '@navi/web-ui/lib/primitives';
import styles from './UploadHistory.module.scss';
import GridRow from '@navi/web-ui/lib/layouts/Grid/GridRow/GridRow';
import GridColumn from '@navi/web-ui/lib/layouts/Grid/GridColumn/GridColumn';
import { AgTable, Pagination } from '@navi/web-ui/lib/components';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '@cp/src/store';
import {
  fetchFile,
  fetchUploadHistory,
  fetchUploadHistoryPolling
} from '../../../AmeyoUtilityActions';
import { DropDownPosition, FILE_ACTION, FILE_STATUS } from '../../../types';
import cx from 'classnames';
import Loader from '@cp/src/components/Loader/Loader';
import { setUploadHistory } from '@cp/src/pages/AmeyoUtility/reducer/ameyoUtilitySlice';
import { PollStatus, poll } from '@cp/src/utils/polling';
import { GLOBAL } from '@cp/src/constants/Global';
import { isFunction } from '@cp/src/utils/commonUtils';
import { GenericObject } from '@cp/src/types/CommonConstans';
import { noop } from '@utils/common';
import { DateFormat, getFormatDate } from '@cp/src/utils/DateHelper';
import { useColumnDefs } from './ColumnDefs';
const UploadHistory = ({
  showUploadDrawer,
  setShowUploadDrawer
}: {
  showUploadDrawer: boolean;
  setShowUploadDrawer: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const dispatch = useDispatch();
  const unsubscribe = useRef<Function>();
  const { columnDefs } = useColumnDefs();
  const [isDataLoading, setIsDataLoading] = useState(false);
  const [pageSize, setPageSize] = useState(10);
  const [pageNumber, setPageNumber] = useState(1);
  const data = useSelector((store: RootState) => store.ameyoUtility.data);
  const pages = useSelector((store: RootState) => store.ameyoUtility.pages);
  const [rowData, setRowData] = useState<Array<GenericObject>>();

  const transformRowData = () => {
    return data?.map((item: GenericObject) => {
      const { createdAt } = item;
      if (createdAt === null) return [];
      const dateString = getFormatDate(createdAt * 1000, DateFormat.DD_MMM_YYYY_hh_mm_a);
      return {
        fileName: item.uploadedFileName,
        uploaded: item.totalCount,
        failed: item.failureCount,
        uploadDateTime: dateString,
        status: item.status,
        file: item.fileReferenceId,
        failureReportExists: item.failureReportExists
      };
    });
  };

  useEffect(() => {
    setRowData([...transformRowData()]);
  }, [data]);

  const handleOnPageChange = (pageNumber: number) => {
    setPageNumber(pageNumber);
  };

  const handleOnPageSizeChange = (pageSize: number) => {
    setPageSize(pageSize);
    setPageNumber(1);
  };

  useEffect(() => {
    if (!showUploadDrawer) {
      setPageNumber(1);
      setPageSize(20);
      return;
    }
    let unsubscribe: Function = noop;
    const pollCallback = (status: PollStatus, response: GenericObject) => {
      if (status === PollStatus.SUCCESS) {
        const isPollingRequired =
          response?.data?.data?.filter(
            (i: GenericObject) =>
              i?.status === FILE_STATUS.INPROGRESS || i?.status === FILE_STATUS.INITIATED
          )?.length > 0;
        dispatch(setUploadHistory(response?.data));
        if (!isPollingRequired && isFunction(unsubscribe)) {
          unsubscribe();
        }
      }
    };

    unsubscribe = poll(
      () => fetchUploadHistoryPolling(pageNumber - 1, pageSize, '', ''),
      data => data,
      GLOBAL.CASE_HISTORY_POLLING_INTERVAL,
      pollCallback
    );

    return () => {
      if (isFunction(unsubscribe)) {
        unsubscribe();
      }
    };
  }, [pageNumber, pageSize, showUploadDrawer]);

  return (
    <Drawer
      headerText="Upload History"
      open={showUploadDrawer}
      onClose={() => {
        setShowUploadDrawer(false);
      }}
      usePadding={false}
      disableOutsideClick={false}
      style={{ width: '743px', overflow: 'auto' }}
    >
      <GridRow className="no-gutters">
        <GridColumn md={12} className={cx(styles.Table__table, 'no-gutters')}>
          <AgTable
            defaultColDef={{
              suppressMovable: true,
              wrapText: true,
              autoHeight: true
            }}
            className="uploadHistoryTable"
            rowData={rowData || []}
            columnDefs={columnDefs}
            headerHeight={28}
            rowHeight={43}
            theme={'alpine'}
            sizeColumnsToFit={true}
            getAgRowStyle={() => {
              return { color: 'var(--grayscale-2)' };
            }}
            paginationWrapperClasses={'paginationWrapperClasses'}
            PaginationComponent={
              <Pagination
                totalCount={pages?.totalElements || 0}
                onPageChange={handleOnPageChange}
                currentPage={pageNumber}
                onPageSizeChange={handleOnPageSizeChange}
                pageSize={pageSize}
                pageNumberDropDownPosition={DropDownPosition.TOP}
              />
            }
          />
        </GridColumn>
      </GridRow>
      <Loader show={isDataLoading} className={'overlay'} animate={false} />
    </Drawer>
  );
};

export default UploadHistory;
